import styled from "styled-components"
import { useMemo } from "react"
import { getCardData } from "@/components/common/Cards"
import { useRouter } from "next/router"
import useDevice from "@/hooks/useDevice"
import { DEVICE_TYPES } from "@/constants"
import PreserveSearchParamsLink from "@/components/common/Links/PreserveSearchParamsLink"
import Image from "@/components/common/Image"
import { getAspectRatioFromImage, getCssSizeValue } from "@/utils/utils"
import Text from "@/components/common/Text"
import { BREAKPOINTS } from "@/styles/themeSettings"

const GridItem = styled(PreserveSearchParamsLink)`
	margin-bottom: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
	position: relative;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ theme, $backgroundColor }) => ($backgroundColor ? $backgroundColor : theme.colors.platinum)};
	transition: transform var(--rgr-duration) var(--ease);
	aspect-ratio: 1;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet || 768}px) {
		margin-bottom: unset;
	}

	&:hover {
		transform: scale(1.01);
	}

	&.highlight {
		grid-row: span 2;
	}
`

const StyledImage = styled(Image)`
	aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || "16/9"};
	width: auto;
	height: 45%;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet || 768}px) {
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.desktop || 1024}px) and (orientation: landscape) {
		height: 48%;
	}

	&.full-size {
		height: 100%;
	}
`

const StyledText = styled(Text)`
	position: absolute;
	z-index: 2;
	bottom: 0;
	left: 0;
	background: ${({ theme }) => theme.colors.richBlue};
	color: ${({ theme }) => theme.colors.platinum};
	width: 100%;
	padding: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)} ${getCssSizeValue(18, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.desktop || 1024}px) and (orientation: landscape) {
		width: auto;
		padding: ${getCssSizeValue(12)} ${getCssSizeValue(32)};
	}
`

function getClassNameForHighlightGridCard(isHighlighted, device) {
	let className = ""

	switch (device) {
		case DEVICE_TYPES.DESKTOP:
			if (isHighlighted) {
				className = "col-desktop-8"
			} else {
				className = "col-desktop-4"
			}
			break
		case DEVICE_TYPES.MOBILE:
			if (isHighlighted) {
				className = "col-mobile-8"
			} else {
				className = "col-mobile-8"
			}
			break
		default:
			if (isHighlighted) {
				className = "col-tablet-4"
			} else {
				className = "col-tablet-2"
			}
	}

	return className
}

export default function HighlightGridCard({ data }) {
	const router = useRouter()
	const { device } = useDevice()

	const { url, cover, header, backgroundColor, isHighlighted, shouldCoverTakeFullSpace } = useMemo(
		() => getCardData(data, router.locale),
		[data, router.locale],
	)

	const gridClassName = getClassNameForHighlightGridCard(isHighlighted, device)
	const aspectRatio = getAspectRatioFromImage(cover)

	return (
		<GridItem href={url} className={`${gridClassName} ${isHighlighted ? "highlight" : ""}`} $backgroundColor={backgroundColor}>
			<StyledImage
				src={cover?.url || ""}
				alt={cover?.alt || ""}
				$aspectRatio={aspectRatio}
				className={`${shouldCoverTakeFullSpace ? "full-size" : ""}`}
			/>
			<StyledText className="typo-strawford typo-strawford-124">{header}</StyledText>
		</GridItem>
	)
}
